import React, { useState, useRef } from "react";
import { SampleQuestion } from "../Interface";
import { Requirements } from "../Entities/JobDescription";
import { InterviewRound } from "../Dto/GenerateJobDescription.dto";
import jobDescriptionService from "../Service/job.description.service";

// Interface for cached sample questions with requirements hash
interface CachedSampleQuestion extends SampleQuestion {
  requirementsHash: string;
}

export interface SampleQuestionsContextType {
  getSampleQuestionsByRound: (
    round: InterviewRound,
    requirements: Requirements[]
  ) => Promise<SampleQuestion[]>;
  getSampleQuestionsForDrawer: (
    round: InterviewRound,
    requirements: Requirements[]
  ) => Promise<SampleQuestion[]>;
  resetSampleQuestions: () => void;
  resetSampleQuestionsForRound: (roundId: string) => void;
}

// Helper function to safely encode Unicode strings to base64
const base64EncodeUnicode = (str: string): string => {
  // First, convert the string to UTF-8 bytes
  const utf8Bytes = new TextEncoder().encode(str);
  // Convert bytes to binary string
  let binaryString = '';
  for (let i = 0; i < utf8Bytes.length; i++) {
    binaryString += String.fromCharCode(utf8Bytes[i]);
  }
  // Use btoa on the binary string (which is now safe for btoa)
  return btoa(binaryString);
};

// Helper function to generate a hash from requirements
const generateRequirementsHash = (requirements: Requirements[]): string => {
  const sortedRequirements = [...requirements]
    .sort((a, b) => a.topic.localeCompare(b.topic))
    .map(req => ({
      topic: req.topic.trim(),
      description: req.description.trim(),
      mustMatch: req.mustMatch
    }));
  
  return base64EncodeUnicode(JSON.stringify(sortedRequirements)).replace(/[^a-zA-Z0-9]/g, '');
};

export const SampleQuestionsContext =
  React.createContext<SampleQuestionsContextType>({
    getSampleQuestionsByRound: async () => [],
    getSampleQuestionsForDrawer: async () => [],
    resetSampleQuestions: () => {},
    resetSampleQuestionsForRound: () => {},
  });

export const SampleQuestionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [cachedQuestions, setCachedQuestions] = useState<CachedSampleQuestion[]>([]);
  const previousRequirementsHash = useRef<string>('');

  const getSampleQuestionsByRound = async (
    round: InterviewRound,
    requirements: Requirements[]
  ) => {
    const roundId = round._id || '';
    const currentRequirementsHash = generateRequirementsHash(requirements);
    
    // Check if requirements have changed globally
    if (previousRequirementsHash.current !== '' && 
        previousRequirementsHash.current !== currentRequirementsHash) {
      // Requirements changed - reset all cached questions
      setCachedQuestions([]);
      console.log('Requirements changed - clearing all cached sample questions');
    }
    
    previousRequirementsHash.current = currentRequirementsHash;

    // Look for cached questions with matching round ID and requirements hash
    const cachedQuestionsForRound = cachedQuestions.filter(
      (question) => 
        question.roundId === roundId && 
        question.requirementsHash === currentRequirementsHash
    );
    
    if (cachedQuestionsForRound.length > 0) {
      console.log(`Using cached sample questions for round ${roundId}`);
      return cachedQuestionsForRound.map(({ requirementsHash, ...question }) => question);
    } else {
      console.log(`Fetching new sample questions for round ${roundId} with updated requirements`);
      const response = await jobDescriptionService.getSampleQuestions(
        requirements,
        round
      );
      
      // Cache the new questions with the requirements hash
      const questionsWithHash: CachedSampleQuestion[] = response.questions.map((question: SampleQuestion) => ({
        ...question,
        requirementsHash: currentRequirementsHash
      }));
      
      setCachedQuestions((prev) => [
        ...prev.filter(q => q.roundId !== roundId), // Remove old questions for this round
        ...questionsWithHash
      ]);
      
      return response.questions;
    }
  };

  // Separate method for drawer that always fetches fresh questions without caching
  const getSampleQuestionsForDrawer = async (
    round: InterviewRound,
    requirements: Requirements[]
  ) => {
    console.log(`Fetching fresh sample questions for drawer - no caching`);
    try {
      const response = await jobDescriptionService.getSampleQuestions(
        requirements,
        round
      );
      return response.questions || [];
    } catch (error) {
      console.error("Error fetching sample questions for drawer:", error);
      return [];
    }
  };

  const resetSampleQuestions = () => {
    console.log('Manually resetting all sample questions');
    setCachedQuestions([]);
    previousRequirementsHash.current = '';
  };

  const resetSampleQuestionsForRound = (roundId: string) => {
    console.log(`Manually resetting sample questions for round ${roundId}`);
    setCachedQuestions((prev) => prev.filter(q => q.roundId !== roundId));
  };

  return (
    <SampleQuestionsContext.Provider
      value={{
        getSampleQuestionsByRound,
        getSampleQuestionsForDrawer,
        resetSampleQuestions,
        resetSampleQuestionsForRound,
      }}
    >
      {children}
    </SampleQuestionsContext.Provider>
  );
};

export const useSampleQuestions = () => {
  const context = React.useContext(SampleQuestionsContext);
  if (!context) {
    throw new Error(
      "useSampleQuestions must be used within a SampleQuestionsProvider"
    );
  }
  return context;
};


